import React from 'react';
import * as Yup from 'yup';
import { Box, Typography } from '@mui/material';
import { Alert, AutoScrollToErrorField, Button, FormikAutoSave, NestableFormik, PsychiatryNoteFormsErrorsAlert, PsychiatryNotesSubmitSections, StickyActionBar, useAlert } from '@uplift/ui-v2';
import { UserSessionInfoCard } from '../../components';
import { useAppointmentByIdQuery, useSessionPsychiatryNoteQuery, useSessionByAppointmentIdQuery, useUserQuery } from '@uplift/queries';
import { submitSession, patchSessionPsychiatryNote, submitSessionForStudent } from '@uplift/api';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router';
import { ROUTING_CONFIG } from '@uplift/constants';
import { SubSystem } from '@uplift/types';


export type PsychiatryNotesPageProps = {
  appointmentId: string;
};


export const PsychiatryNotesPageContent: React.FC<PsychiatryNotesPageProps> = (props) => {
  const history = useHistory();
  const alert = useAlert();
  const queryClient = useQueryClient();
  const { data: appointment } = useAppointmentByIdQuery(props.appointmentId, { suspense: true });
  const { data: patient } = useUserQuery(appointment?.patientId, { suspense: true, enabled: !!appointment?.patientId });
  const { data: session } = useSessionByAppointmentIdQuery(props.appointmentId, { suspense: true });
  const { data: psychiatryNote } = useSessionPsychiatryNoteQuery(session?.id, { suspense: true, cacheTime: 0, refetchOnMount: 'always' });

  const hasErrors = React.useMemo(() => {
    const sections = Object.keys(psychiatryNote?.forms ?? {}).map(k => psychiatryNote?.forms[k]);

    return sections.some(s => s.errors.length > 0);
  }, [ psychiatryNote ]);

  const initialValues = React.useMemo(() => {
    return {
      submit: null,
      primaryCptCode: session?.cptCodeId ?? undefined,
      addonCptCodes: session?.addonCptCodeIds ?? [],
      diagnosis: [] as string[],
      identityVerified: null,
    };
  }, [ session ]);

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      await patchSessionPsychiatryNote(session.id, {
        primary_cpt_code_id: values.primaryCptCode,
        addon_cpt_code_ids: values.addonCptCodes,
      });

      if (values.submit === 'now') {
        if (patient.subsystem === SubSystem.higherEd) {
          await submitSessionForStudent(session.id, { identityVerified: values.identityVerified ?? null });
        } else {
          await submitSession(session.id, { identityVerified: values.identityVerified ?? null });
        }
        history.push(ROUTING_CONFIG.main);
      } else if (values.submit === 'later') {
        history.push(ROUTING_CONFIG.notSubmittedSessions);
      }

      queryClient.invalidateQueries([ 'appointment', props.appointmentId, 'session' ]);
      queryClient.invalidateQueries([ 'session', session.id ]);
    } catch (error) {
      const errMsg = error?.response?.data?.error;

      alert.error(errMsg);
      console.error(error);
    }
  };

  return (
    <NestableFormik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        primaryCptCode: Yup.string().nullable().when('submit', {
          is: 'now',
          then: f => f.required('Select a primary CPT code'),
        }),
        addonCptCodes: Yup.array(Yup.string()).nullable(),
      })}
      onSubmit={async (values) => {
        await handleSubmit(values);
      }}
    >
      {formik => (
        <Box>
          <AutoScrollToErrorField />
          <FormikAutoSave
            onBeforeSave={async () => {
              await formik.setFieldValue('submit', null);
            }}
          />
          <Box maxWidth="1000px" width="100%" margin="0 auto" mb={8} px={2} boxSizing="border-box">
            <Box mt={8} maxWidth="650px">
              <Typography variant="h2">Session Note</Typography>
            </Box>
            <Box mt={3} maxWidth="650px">
              <PsychiatryNoteFormsErrorsAlert forms={psychiatryNote.forms} />
            </Box>
            <Box mt={2} maxWidth="650px">
              <UserSessionInfoCard appointment={appointment} showActions={false} />
            </Box>
            <Box mt={2}>
              <PsychiatryNotesSubmitSections
                patientId={appointment.patientId}
                sessionId={session.id}
                appointmentId={session.appointmentId}
                providerId={appointment.providerId}
              />
            </Box>
          </Box>
          <StickyActionBar>
            {hasErrors ? <Alert severity="error">Check for missing information before submitting the session</Alert> : null}
            <Button
              size="large" color="secondary" variant="outlined"
              disabled={formik.isSubmitting}
              onClick={async () => {
                await formik.setFieldValue('submit', 'later');
                formik.setSubmitting(true);
                await handleSubmit({ ...formik.values, submit: 'later' });
                formik.setSubmitting(false);
              }}
            >
              Save and Finish later
            </Button>
            <Button
              size="large" color="secondary" variant="contained"
              disabled={formik.isSubmitting || hasErrors}
              onClick={async () => {
                await formik.setFieldValue('submit', 'now');
                formik.handleSubmit();
              }}
            >
              Submit and Sign
            </Button>
          </StickyActionBar>
        </Box>
      )}
    </NestableFormik>
  );
};
